import { useEffect, useRef } from "react";
import { createRGBA } from "../Player/rgbaEngine";

const APP_CRT_SHADER = `
float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

void main() {
    vec2 uv = gl_FragCoord.xy / resolution.xy;

    float scanA = sin((uv.y * resolution.y * 0.22) - (time * 5.0)) * 0.042;
    float scanB = sin((uv.y * resolution.y * 0.06) + (time * 1.8)) * 0.028;
    float grain = (hash(gl_FragCoord.xy + vec2(time * 37.0, time * 19.0)) - 0.5) * 0.06;
    float flicker = 0.92 + 0.08 * sin(time * 6.3);

    float stripe = step(0.5, fract(uv.y * resolution.y * 0.5));
    vec3 tint = mix(tintA, tintB, 0.62);
    vec3 lineTint = mix(tint * 0.8, tint * 1.12, stripe);

    vec3 pass = lineTint * (scanA + scanB + grain) * flicker;
    gl_FragColor = vec4(pass, 0.17);
}
`;

function parseCssColorToRgbVector(value, fallback) {
  if (!value || typeof value !== "string") return fallback;
  const raw = value.trim();

  const hex = raw.startsWith("#") ? raw.slice(1) : raw;
  const normalizedHex =
    hex.length === 3 ? hex.split("").map((part) => `${part}${part}`).join("") : hex;
  if (/^[0-9a-fA-F]{6}$/.test(normalizedHex)) {
    return [
      Number.parseInt(normalizedHex.slice(0, 2), 16) / 255,
      Number.parseInt(normalizedHex.slice(2, 4), 16) / 255,
      Number.parseInt(normalizedHex.slice(4, 6), 16) / 255,
    ];
  }

  const rgbMatch = raw.match(/rgba?\(([^)]+)\)/i);
  if (rgbMatch) {
    const parts = rgbMatch[1].split(",").map((part) => Number.parseFloat(part.trim()));
    if (parts.length >= 3 && parts.every((part, index) => index > 2 || Number.isFinite(part))) {
      return [
        Math.max(0, Math.min(255, parts[0])) / 255,
        Math.max(0, Math.min(255, parts[1])) / 255,
        Math.max(0, Math.min(255, parts[2])) / 255,
      ];
    }
  }

  return fallback;
}

export default function AppCrtOverlay() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const target = canvasRef.current;
    if (!target) return undefined;

    const rootStyles = getComputedStyle(document.documentElement);
    const tintA = parseCssColorToRgbVector(
      rootStyles.getPropertyValue("--primary-dark"),
      [0.1, 0.16, 0.22]
    );
    const tintB = parseCssColorToRgbVector(
      rootStyles.getPropertyValue("--primary-light"),
      [0.35, 0.66, 0.84]
    );

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const shader = createRGBA(APP_CRT_SHADER, {
      target,
      loop: !prefersReducedMotion,
      uniforms: {
        tintA: () => tintA,
        tintB: () => tintB,
      },
    });

    if (prefersReducedMotion) {
      shader.setUniform?.("tintA", tintA);
      shader.setUniform?.("tintB", tintB);
    }

    return () => {
      shader.destroy();
    };
  }, []);

  return (
    <div className="app-crt-overlay" aria-hidden="true">
      <canvas ref={canvasRef} className="app-crt-overlay-canvas" />
    </div>
  );
}

